import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import {
  IMMUNOSUPPRESSION_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";
import { mappers } from "../../utils/attributeMapping/mappers";

/**
 * Pre-screening questions for Sequiris Influenza Vaccine Study
 * Target population: Adults 65+ interested in influenza vaccine clinical trial
 */
export const sequirisFluVaccineScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "64_or_under",
        label: "64 or under",
        disqualify: true
      },
      {
        value: "65_or_older",
        label: "65 or older"
      },
    ],
    disqualifyMessage: "This study is for adults aged 65 and older.",
    attributeMapping: mappers.value({
      "64_or_under": [],
      "65_or_older": [],
    }),
  },

  {
    id: "recent_flu_vaccine",
    type: QuestionType.RADIO,
    question: "Have you had an influenza vaccine in the past 6 months?",
    helpText: "This includes any flu vaccine such as Fluad, Fluzone, Fluarix, Afluria, or any other seasonal influenza vaccination.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had an influenza vaccine in the past 6 months (since April 2025)",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had an influenza vaccine in the past 6 months (since April 2025)"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received an influenza vaccine in the past 6 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23449, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 23449, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "medical_conditions_exclusion",
    type: QuestionType.CHECKBOX,
    question: "Have you ever been diagnosed with any of the following conditions?",
    helpText: "Please select all that apply. If you have not been diagnosed with any of these conditions, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "guillain_barre",
        label: "Guillain-Barré syndrome (autoimmune disorder that causes inflammation and demyelination of the nerves)",
        disqualify: true
      },
      {
        value: "encephalomyelitis",
        label: "Encephalomyelitis (inflammation of the brain and spinal cord from an infection or autoimmune disorder)",
        disqualify: true
      },
      {
        value: "transverse_myelitis",
        label: "Transverse Myelitis (inflammation across the spinal cord caused by an infection or autoimmune disorder)",
        disqualify: true
      },
      {
        value: "immunocompromise",
        label: "Weakened immune system (e.g. as a result of HIV, cancer, chemotherapy, steroids)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        guillain_barre: [
          { attributeDefinitionId: 25742, booleanAttributeValue: true },
        ],
        encephalomyelitis: [
          { attributeDefinitionId: 26806, booleanAttributeValue: true },
        ],
        transverse_myelitis: [
          { attributeDefinitionId: 26804, booleanAttributeValue: true },
        ],
        immunocompromise: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_IMMUNODEFICIENCY, booleanAttributeValue: true },
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "allergic_reaction_flu_vaccine",
    type: QuestionType.RADIO,
    question: "Have you ever had a severe allergic reaction or anaphylaxis to a flu vaccination?",
    helpText: "Anaphylaxis is a severe allergic reaction that usually requires medical treatment with adrenaline (epinephrine).",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had a severe allergic reaction or anaphylaxis to a flu vaccination",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had a severe allergic reaction or anaphylaxis to a flu vaccination"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of severe allergic reactions to flu vaccines cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26802, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 26802, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_medications",
    type: QuestionType.CHECKBOX,
    question: "Have you recently received any of the following medications?",
    helpText: "Please select all that apply. If you have not received any of these medications, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "ivig_6_months",
        label: "I have received IVIg (intravenous immunoglobulin) in the past 6 months",
        disqualify: true
      },
      {
        value: "blood_transfusion_6_months",
        label: "I have received a blood transfusion (including plasma, platelets, or whole blood) in the past 6 months",
        disqualify: true
      },
      {
        value: "high_dose_steroids_3_months",
        label: "I have taken high dose steroids (such as Prednisone >20mg per day) for more than 14 days in the past 3 months",
        disqualify: true
      },
      {
        value: "cancer_treatment_3_months",
        label: "I have been treated for cancer within the past 3 months (with chemotherapy, radiation therapy or immunotherapy)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above applies to me"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants who have recently received these medications or treatments.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        ivig_6_months: [
          { attributeDefinitionId: 21642, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        blood_transfusion_6_months: [
          { attributeDefinitionId: 25074, booleanAttributeValue: true },
        ],
        high_dose_steroids_3_months: [
          { attributeDefinitionId: 25582, booleanAttributeValue: true },
        ],
        cancer_treatment_3_months: [
          { attributeDefinitionId: 25383, booleanAttributeValue: true },
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am currently enrolling in or participating in another clinical trial OR I received medication as part of a clinical trial within the past 30 days",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I am not currently enrolling in or participating in another clinical trial"
      },
    ],
    disqualifyMessage: "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
    }),
  },
];